import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import * as THREE from 'three';
import { useEditorStore } from '.';
import useFinalizationDataStore from './useFinalizationDataStore';

const useElongationStore = create(
  devtools((set, get) => ({
    // Simplified Elongation Tool - only essential fields
    enabled: false,
    value: 0.0,
    unit: 'mm',
    isElongationApplied: false,
    previewMesh: null,
    isAppliedFromDraft: false,

    // Actions
    setElongation: (data) => set({ ...get(), ...data }),
    setPreviewMesh: (mesh) => {
      set({ previewMesh: mesh });
    },

    currentElongationCommand: null,
    setCurrentElongationCommand: (command) => set({ currentElongationCommand: command }),

    createElongationCommand: () => {
      const { enabled, value, unit, previewMesh } = get();
      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();
      const originalMaterial = sceneRefs.material;

      if (!previewMesh || !enabled || !(parseFloat(value || 0) > 0)) return null;

      const originalMesh = sceneRefs.shadedMesh;
      const originalGeometry = originalMesh.geometry;
      const originalMatrix = originalMesh.matrix.clone();
      // const originalMaterial = originalMesh.material;
      const originalElongation = { enabled, value, unit };
      console.log('🔍 DEBUG: Creating elongation command with originalElongation:', originalElongation);

      // Clone geometry and matrix from preview (finalized result)
      const cloneGeom = new THREE.BufferGeometry().copy(previewMesh.geometry);
      const cloneMatrix = new THREE.Matrix4().copy(previewMesh.matrix);

      return {
        execute: () => {
          // Remove preview visual
          const latest = get().previewMesh;
          if (latest && latest.isMesh) {
            latest.parent?.remove(latest);
            latest.geometry?.dispose?.();
            latest.material?.dispose?.();
          }

          const newMesh = new THREE.Mesh(cloneGeom, originalMaterial);
          newMesh.matrix.copy(cloneMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          newMesh.matrix.decompose(newMesh.position, newMesh.quaternion, newMesh.scale);
          newMesh.matrixAutoUpdate = true;
          replaceShadedMesh(newMesh);

          if(previewMesh)
          {
            previewMesh.parent?.remove(previewMesh);
            previewMesh.geometry?.dispose?.();
            previewMesh.material?.dispose?.();
          }
          // Save to finalization store
          useFinalizationDataStore.getState().saveElongation(
            originalElongation.value,
            originalElongation.unit
          );

          console.log('🔍 DEBUG: Executing elongation command');
          set({
            enabled: false,
            value: 0.0,
            unit: 'mm',
            isElongationApplied: true,
            previewMesh: null
          });
          const afterState = get();
          console.log('🔍 DEBUG: After execute, elongation store state:', afterState);
        },
        undo: () => {
          const restoredMesh = new THREE.Mesh(originalGeometry, originalMaterial);
          restoredMesh.matrix.copy(originalMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
          restoredMesh.matrixAutoUpdate = true;
          replaceShadedMesh(restoredMesh);
          set({
            enabled: originalElongation.enabled,
            value: originalElongation.value,
            unit: originalElongation.unit,
            isElongationApplied: false
          });
        }
      };
    },

    // Reset all data
    reset: () => {
      console.log('🔍 DEBUG: Resetting elongation store');
      set({
        enabled: false,
        value: 8.0,
        unit: 'mm',
        isElongationApplied: false,
        previewMesh: null
      });
    }
  }))
);

export default useElongationStore;